import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  CATEGORY_MAP, DEFAULT_LEGEND, TEMPLATE_MAP, getCategoryConfig, CLOSURE_TYPES,
} from '../lib/categories';
import {
  AlertTriangle, Trash2, Paperclip, Plus, Shield, CheckCircle2, Clock, UserPlus, ChevronDown,
} from 'lucide-react';
import { useBoards } from '../context/BoardContext';
import {
  createIssue, updateIssue, deleteIssue, listAnalyses, createAnalysis,
  listTasks, createTask, updateTask, deleteTask,
} from '../lib/api';
import { toast } from '../hooks/use-toast';
import TemplatePickerDialog from './TemplatePickerDialog';
import AnalysisEditorDialog from './AnalysisEditorDialog';
import { toISODate } from '../lib/date';
import { usePermissions } from '../context/PermissionsContext';

const IssueEditDialog = ({
  open, onOpenChange, categoryKey, date, existing, onSaved, onDeleted,
}) => {
  const { activeBoard, currentUserEmail } = useBoards();
  const perms = usePermissions();
  const canEdit = perms.canEdit(categoryKey);
  const isNew = !existing?.id;
  const colorEditable = isNew || perms.canChangeColor;
  const cat = CATEGORY_MAP[categoryKey];
  const categoryConfig = getCategoryConfig(activeBoard, categoryKey);
  const statuses = categoryConfig?.statuses || DEFAULT_LEGEND;
  const fields = categoryConfig?.fields || [];
  const catStatuses = activeBoard?.category_configs?.[categoryKey]?.statuses || [];
  const conditionFor = (colorKey) => (catStatuses.find((s) => s.key === colorKey) || {}).condition || '';
  const teamMembers = activeBoard?.team_members || [];
  // team_members may be legacy strings or {email, role, assigned_categories}
  // objects — always normalise to email strings before rendering, otherwise
  // React tries to render an object as a child (error #31 -> blank page).
  const memberEmails = teamMembers
    .map((m) => (typeof m === 'string' ? m : m?.email))
    .filter(Boolean);
  const ownerOptions = Array.from(new Set([
    ...(activeBoard?.owner_email ? [activeBoard.owner_email] : []),
    ...memberEmails,
    ...(currentUserEmail ? [currentUserEmail] : []),
  ]));
  const taskAssigneeOptions = Array.from(new Set([
    ...(activeBoard?.owner_email ? [activeBoard.owner_email] : []),
    ...memberEmails,
  ]));
  const defaultIssueStatus =
    statuses.find((s) => s.key === 'red')?.key ||
    statuses.find((s) => s.key !== 'green' && s.key !== 'grey')?.key ||
    statuses[0]?.key ||
    'red';

  const [color, setColor] = useState(defaultIssueStatus);
  const [details, setDetails] = useState({});
  const [repeatIssue, setRepeatIssue] = useState(false);
  const [notes, setNotes] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [status, setStatus] = useState('open');
  const [closureType, setClosureType] = useState('24h');

  // Countermeasures
  const [tempCM, setTempCM] = useState('');
  const [tempCMDate, setTempCMDate] = useState('');
  const [permCM, setPermCM] = useState('');
  const [permCMDate, setPermCMDate] = useState('');
  const [isClosed, setIsClosed] = useState(false);

  const [saving, setSaving] = useState(false);

  // Attached analyses
  const [attached, setAttached] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [analysisEditor, setAnalysisEditor] = useState({ open: false, doc: null });

  // Closing tasks
  const [tasks, setTasks] = useState([]);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskAssignee, setTaskAssignee] = useState('');
  const [taskDate, setTaskDate] = useState('');

  useEffect(() => {
    if (open) {
      setColor(existing?.color || defaultIssueStatus);
      setDetails(existing?.details || {});
      setRepeatIssue(existing?.repeat_issue || false);
      setNotes(existing?.notes || '');
      setOwnerEmail(existing?.owner_email || currentUserEmail || '');
      if (existing?.target_date) {
        setTargetDate(existing.target_date);
      } else {
        const t = new Date(date);
        t.setDate(t.getDate() + 1);
        setTargetDate(toISODate(t));
      }
      setStatus(existing?.status || 'open');
      setClosureType(existing?.closure_type || '24h');
      setTempCM(existing?.temporary_cm || '');
      setTempCMDate(existing?.temporary_cm_date || '');
      setPermCM(existing?.permanent_cm || '');
      setPermCMDate(existing?.permanent_cm_date || '');
      setIsClosed(existing?.is_closed || false);
    }
  }, [open, existing, defaultIssueStatus, currentUserEmail, date]);

  const loadAttached = useCallback(async () => {
    if (!open || !activeBoard || !existing?.id) {
      setAttached([]);
      return;
    }
    try {
      const analyses = await listAnalyses({ board_id: activeBoard.id, card_id: existing.id });
      setAttached(
        analyses.map((a) => ({
          id: a.id,
          templateKey: a.template_type,
          title: a.title,
          status: a.status,
          raw: a,
        }))
      );
    } catch (e) {
      console.error(e);
    }
  }, [open, activeBoard, existing]);

  useEffect(() => {
    loadAttached();
  }, [loadAttached]);

  const loadTasks = useCallback(async () => {
    if (!open || !existing?.id) {
      setTasks([]);
      return;
    }
    try {
      setTasks(await listTasks({ issue_id: existing.id }));
    } catch (e) {
      console.error(e);
    }
  }, [open, existing]);

  useEffect(() => {
    loadTasks();
    // Reset the "add task" row whenever the open issue changes.
    setTaskTitle('');
    setTaskAssignee('');
    setTaskDate('');
  }, [loadTasks]);

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      await loadTasks();
    } catch (e) {
      toast({ title: 'Failed to update task', description: e.message });
    }
  };

  const handleTaskDelete = async (taskId) => {
    if (!confirm('Delete this task?')) return;
    try {
      await deleteTask(taskId);
      await loadTasks();
    } catch (e) {
      toast({ title: 'Failed to delete task', description: e.message });
    }
  };

  const handleAddTask = async () => {
    if (!activeBoard || !existing?.id || !taskTitle.trim()) return;
    try {
      await createTask({
        board_id: activeBoard.id,
        issue_id: existing.id,
        title: taskTitle.trim(),
        owner_email: taskAssignee,
        target_date: taskDate,
        category: categoryKey,
        status: 'open',
      });
      setTaskTitle('');
      setTaskAssignee('');
      setTaskDate('');
      await loadTasks();
    } catch (e) {
      toast({ title: 'Failed to add task', description: e.message });
    }
  };

  const tasksDoneCount = tasks.filter((t) => t.status === 'done').length;

  if (!cat) return null;

  const isIssueLike = color !== 'green' && color !== 'grey';

  const handleSave = async () => {
    if (!activeBoard) return;
    if (isIssueLike && (!ownerEmail || !targetDate)) {
      toast({
        title: 'Owner & target date required',
        description: 'Assign a responsible member and target date before saving.',
      });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        date, category: categoryKey, color,
        details: isIssueLike ? details : {},
        repeat_issue: repeatIssue, notes,
        owner_email: ownerEmail,
        target_date: targetDate,
        status: isClosed ? 'closed' : status,
        closure_type: closureType,
        updated_by: currentUserEmail,
        temporary_cm: tempCM,
        temporary_cm_date: tempCMDate,
        permanent_cm: permCM,
        permanent_cm_date: permCMDate,
        is_closed: isClosed,
      };
      let saved;
      if (existing?.id) {
        saved = await updateIssue(existing.id, payload);
      } else {
        saved = await createIssue(activeBoard.id, payload);
      }
      toast({
        title: existing ? 'Issue updated' : 'Issue created',
        description: `${cat.label} · ${date}`,
      });
      onSaved && onSaved(saved);
    } catch (e) {
      toast({ title: 'Failed to save', description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existing?.id) return;
    if (!confirm('Delete this issue permanently?')) return;
    try {
      await deleteIssue(existing.id);
      toast({ title: 'Issue deleted' });
      onDeleted && onDeleted();
    } catch (e) {
      toast({ title: 'Failed', description: e.message });
    }
  };

  const toggleMulti = (name, opt) => setDetails((d) => {
    const cur = Array.isArray(d[name]) ? d[name] : [];
    return { ...d, [name]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  const handleTemplatePicked = async (templateKey) => {
    if (!activeBoard || !existing?.id) {
      toast({ title: 'Save first', description: 'Save the issue before attaching a template.' });
      return;
    }
    const template = TEMPLATE_MAP[templateKey];
    const title = `${template.label} – ${cat.label} on ${date}`;
    try {
      const created = await createAnalysis({
        board_id: activeBoard.id,
        card_id: existing.id,
        template_type: templateKey,
        title,
        owner_email: ownerEmail || currentUserEmail || activeBoard.owner_email,
        status: 'draft',
        data: {},
      });
      setPickerOpen(false);
      await loadAttached();
      setAnalysisEditor({
        open: true,
        doc: {
          id: created.id, templateKey, title: created.title, status: created.status, raw: created,
        },
      });
    } catch (err) {
      toast({ title: 'Failed', description: err.message });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[680px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center text-white text-sm font-bold"
                style={{ background: cat.color }}
              >
                {cat.key}
              </div>
              <div>
                <div>{existing ? 'Edit issue' : 'New issue'} — {cat.label}</div>
                <DialogDescription className="text-xs mt-0.5">{date}</DialogDescription>
              </div>
              {isClosed && (
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-green-100 text-green-800 border border-green-300">
                  <CheckCircle2 className="h-3 w-3" />
                  CLOSED
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {!canEdit && (
            <div className="rounded-md bg-slate-100 border border-slate-300 p-3 text-xs text-slate-700">
              <strong>Read-only:</strong> You are not assigned to the {cat.label} category. Ask the
              board owner or an admin to assign you if you need to edit.
            </div>
          )}

          {/* Status selector */}
          <div className="space-y-2">
            <Label>Status color</Label>
            {colorEditable ? (
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${Math.min(statuses.length, 4)}, minmax(0, 1fr))` }}
              >
                {statuses.map((c) => {
                  const active = color === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setColor(c.key)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                        active ? 'border-slate-900 shadow-sm' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      style={{ background: active ? c.bg : 'white' }}
                    >
                      <div
                        className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                        style={{ background: c.color }}
                      />
                      <span className="text-[11px] font-medium text-slate-700 text-center leading-tight">
                        {c.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div
                className="flex items-center gap-2 p-3 rounded-lg border-2 border-slate-200 bg-slate-50 w-fit"
                title="Only an admin can change the color once set."
              >
                <div
                  className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                  style={{ background: (statuses.find((s) => s.key === color) || {}).color || '#94a3b8' }}
                />
                <span className="text-[11px] font-medium text-slate-700">
                  {(statuses.find((s) => s.key === color) || {}).label || color}
                </span>
              </div>
            )}
            {conditionFor(color) && (
              <p className="text-xs text-slate-500 italic">{conditionFor(color)}</p>
            )}
          </div>

          {/* Assignment (required for non-green) */}
          {isIssueLike && (
            <div className="pt-2 border-t border-slate-100 rounded-lg bg-blue-50/60 border-2 border-blue-200 p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                <UserPlus className="h-4 w-4" />
                Assignment & Closure Tracking
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Responsible member (email)</Label>
                  {ownerOptions.length > 1 ? (
                    <div className="flex gap-1 mt-1">
                      <Input
                        type="email"
                        value={ownerEmail}
                        onChange={(e) => setOwnerEmail(e.target.value)}
                        className="h-8 flex-1"
                        placeholder="owner@company.com"
                      />
                      <Select value="" onValueChange={setOwnerEmail}>
                        <SelectTrigger className="h-8 w-9 px-0 justify-center">
                          <ChevronDown className="h-3 w-3" />
                        </SelectTrigger>
                        <SelectContent>
                          {ownerOptions.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <Input
                      type="email"
                      value={ownerEmail}
                      onChange={(e) => setOwnerEmail(e.target.value)}
                      className="h-8 mt-1"
                      placeholder="owner@company.com"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs">Target date</Label>
                  <Input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="h-8 mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Closure type</Label>
                  <Select value={closureType} onValueChange={setClosureType}>
                    <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CLOSURE_TYPES.map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Progress status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Issue detail fields */}
          {isIssueLike && (
            <div className="space-y-4 pt-2 border-t border-slate-100">
              <div className="text-sm font-semibold text-slate-800">Issue Details</div>
              {fields.length === 0 ? (
                <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-md py-3 px-3">
                  No detail fields configured for {cat.label}. Add them in Board Settings.
                </div>
              ) : (
                fields.map((field) => (
                  <div key={field.name}>
                    <Label htmlFor={field.name} className="text-sm">
                      {field.label}
                    </Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={field.name}
                        value={details[field.name] || ''}
                        onChange={(e) => setDetails({ ...details, [field.name]: e.target.value })}
                        rows={2}
                        className="mt-1.5"
                      />
                    ) : field.type === 'select' ? (
                      <Select
                        value={details[field.name] || ''}
                        onValueChange={(v) => setDetails({ ...details, [field.name]: v })}
                      >
                        <SelectTrigger id={field.name} className="mt-1.5">
                          <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {(field.options || []).map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : field.type === 'multiselect' ? (
                      <div className="mt-1.5 space-y-1.5">
                        {(field.options || []).map((opt) => {
                          const cur = Array.isArray(details[field.name]) ? details[field.name] : [];
                          return (
                            <div key={opt} className="flex items-center gap-2">
                              <Checkbox
                                id={`${field.name}-${opt}`}
                                checked={cur.includes(opt)}
                                onCheckedChange={() => toggleMulti(field.name, opt)}
                              />
                              <Label
                                htmlFor={`${field.name}-${opt}`}
                                className="text-sm font-normal cursor-pointer"
                              >
                                {opt}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <Input
                        id={field.name}
                        type={field.type}
                        value={details[field.name] || ''}
                        onChange={(e) => setDetails({ ...details, [field.name]: e.target.value })}
                        className="mt-1.5"
                      />
                    )}
                  </div>
                ))
              )}

              <div className="flex items-center gap-3 p-3 rounded-md bg-amber-50 border border-amber-200">
                <Checkbox
                  id="repeat-issue"
                  checked={repeatIssue}
                  onCheckedChange={(v) => setRepeatIssue(!!v)}
                />
                <Label
                  htmlFor="repeat-issue"
                  className="flex items-center gap-2 text-sm cursor-pointer font-medium text-amber-900"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Repeat Issue — flag this as recurring
                </Label>
              </div>

              {/* CM tracking */}
              <div className="rounded-lg border border-slate-200 divide-y">
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-500" />
                    <div className="text-sm font-semibold text-slate-800">Temporary CM</div>
                    {tempCM && (
                      <span className="ml-auto text-[10px] font-semibold text-orange-800 bg-orange-100 px-1.5 py-0.5 rounded uppercase">
                        Applied
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Textarea
                      value={tempCM}
                      onChange={(e) => setTempCM(e.target.value)}
                      placeholder="Quick action taken"
                      rows={2}
                      className="col-span-2"
                    />
                    <div>
                      <Label className="text-[10px] text-slate-500">Applied</Label>
                      <Input
                        type="date"
                        value={tempCMDate}
                        onChange={(e) => setTempCMDate(e.target.value)}
                        className="h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <div className="text-sm font-semibold text-slate-800">Permanent CM</div>
                    {permCM && (
                      <span className="ml-auto text-[10px] font-semibold text-blue-800 bg-blue-100 px-1.5 py-0.5 rounded uppercase">
                        Applied
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Textarea
                      value={permCM}
                      onChange={(e) => setPermCM(e.target.value)}
                      placeholder="Long-term solution"
                      rows={2}
                      className="col-span-2"
                    />
                    <div>
                      <Label className="text-[10px] text-slate-500">Applied</Label>
                      <Input
                        type="date"
                        value={permCMDate}
                        onChange={(e) => setPermCMDate(e.target.value)}
                        className="h-8"
                      />
                    </div>
                  </div>
                </div>
                {perms.canCloseIssues && (
                  <div className="p-3 flex items-center gap-3 bg-slate-50">
                    <Checkbox
                      id="is-closed"
                      checked={isClosed}
                      onCheckedChange={(v) => setIsClosed(!!v)}
                    />
                    <Label
                      htmlFor="is-closed"
                      className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer"
                    >
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Mark this issue as CLOSED
                    </Label>
                    {existing?.is_closed && existing?.closed_date && (
                      <span className="ml-auto text-[11px] text-slate-500">
                        Closed on {existing.closed_date} by {existing.closed_by}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Analysis attachments */}
              {existing?.id && (
                <div className="pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                      <Paperclip className="h-4 w-4 text-slate-500" />
                      Data Analyses
                      {attached.length > 0 && (
                        <span className="text-xs text-slate-500 font-normal">
                          ({attached.length})
                        </span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPickerOpen(true)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Attach template
                    </Button>
                  </div>
                  {attached.length === 0 ? (
                    <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-md py-3 px-3 text-center">
                      Attach A3, 5-Why, Fishbone… templates for root-cause analysis.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {attached.map((a) => {
                        const tpl = TEMPLATE_MAP[a.templateKey];
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => setAnalysisEditor({ open: true, doc: a })}
                            className="w-full flex items-center gap-3 rounded-md border border-slate-200 hover:border-[#0090D8] hover:bg-sky-50 p-2 text-left transition-colors"
                          >
                            <div
                              className="w-8 h-8 rounded-md flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                              style={{ background: tpl?.color || '#64748b' }}
                            >
                              {tpl?.label.split(' ').map((w) => w[0]).slice(0, 2).join('') || 'AN'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">
                                {a.title}
                              </div>
                              <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                <span>{tpl?.label || a.templateKey}</span>
                                <span>·</span>
                                <span className="capitalize">{a.status}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Closing tasks */}
              {existing?.id && (
                <div className="pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-slate-500" />
                      Tasks to close this issue
                    </div>
                    {tasks.length > 0 && (
                      <span className="text-xs text-slate-500 font-normal">
                        {tasksDoneCount} of {tasks.length} done
                      </span>
                    )}
                  </div>

                  {tasks.length === 0 ? (
                    <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-md py-3 px-3 text-center mb-2">
                      No tasks yet. Add tasks below to track what needs to happen to close this issue.
                    </div>
                  ) : (
                    <div className="space-y-2 mb-3">
                      {tasks.map((t) => (
                        <div
                          key={t.id}
                          className="flex items-center gap-2 rounded-md border border-slate-200 p-2"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate">
                              {t.title}
                            </div>
                            <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                              <span>{t.owner_email || 'Unassigned'}</span>
                              {t.target_date && (
                                <>
                                  <span>·</span>
                                  <span>{t.target_date}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <Select
                            value={t.status}
                            onValueChange={(v) => handleTaskStatusChange(t.id, v)}
                            disabled={!canEdit}
                          >
                            <SelectTrigger className="h-8 w-[130px] flex-shrink-0">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="done">Done</SelectItem>
                            </SelectContent>
                          </Select>
                          {canEdit && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 flex-shrink-0 text-red-500 hover:bg-red-50 hover:text-red-600"
                              onClick={() => handleTaskDelete(t.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {canEdit && (
                    <div className="grid grid-cols-12 gap-2">
                      <Input
                        className="col-span-4 h-8"
                        placeholder="Task title"
                        value={taskTitle}
                        onChange={(e) => setTaskTitle(e.target.value)}
                      />
                      <Select value={taskAssignee} onValueChange={setTaskAssignee}>
                        <SelectTrigger className="col-span-3 h-8">
                          <SelectValue placeholder="Assignee" />
                        </SelectTrigger>
                        <SelectContent>
                          {taskAssigneeOptions.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="date"
                        className="col-span-3 h-8"
                        value={taskDate}
                        onChange={(e) => setTaskDate(e.target.value)}
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="col-span-2 h-8"
                        disabled={!taskTitle.trim()}
                        onClick={handleAddTask}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="pt-2 border-t border-slate-100">
            <Label htmlFor="notes" className="text-sm">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1.5"
              placeholder="Additional context..."
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            {existing && canEdit && (
              <Button
                type="button"
                variant="outline"
                onClick={handleDelete}
                disabled={saving}
                className="text-red-500 border-red-200 hover:bg-red-50 mr-auto"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button
              onClick={handleSave}
              disabled={saving || !canEdit}
              title={!canEdit ? 'You are not assigned to this category' : ''}
              className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
            >
              {saving ? 'Saving...' : (existing ? 'Save changes' : 'Create issue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemplatePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={handleTemplatePicked}
      />

      <AnalysisEditorDialog
        open={analysisEditor.open}
        onOpenChange={(v) => setAnalysisEditor((s) => ({ ...s, open: v }))}
        doc={analysisEditor.doc}
        onSaved={() => loadAttached()}
      />
    </>
  );
};

export default IssueEditDialog;
